import {
	BETTER_AUTH_BASEPATH,
	BETTER_AUTH_BASEPATH_USER,
	basePath,
} from "@Common/constants";
import { isSubscriptionLimitError } from "@Common/error_codes";
import type { CustomSession, LoginMethod } from "@Common/types/better_auth";
import { type ErrorType, loginRedirectPath } from "@Src/constants";
import { magicLinkClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import type { AuthProvider } from "react-admin";
import { match } from "ts-pattern";

const authClient = createAuthClient({
	baseURL: window.origin,
	basePath: BETTER_AUTH_BASEPATH,
	plugins: [magicLinkClient()],
});

// Separate auth client for LinkedIn authentication (used in public forms)
export const linkedInAuthClient = createAuthClient({
	baseURL: window.origin,
	basePath: BETTER_AUTH_BASEPATH_USER,
});

let cachedSession: CustomSession | null = null;
let sessionPromise: Promise<CustomSession | null> | null = null;
let lastFetchedSession = 0;
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
let isLoggingOut = false;

const clearSessionCache = () => {
	cachedSession = null;
	sessionPromise = null;
	lastFetchedSession = 0;
};

const getCachedSession = async () => {
	const now = Date.now();

	if (cachedSession && now - lastFetchedSession < CACHE_DURATION_MS) {
		return Promise.resolve(cachedSession);
	}

	// If a fetch is already in progress, return that promise
	if (sessionPromise) {
		return sessionPromise;
	}

	// Start a new fetch and cache the promise
	sessionPromise = (async () => {
		const { data: session } = await authClient.getSession();

		// typecast is required because inferred type from authClient.getSession() uses better-auth standard type
		// but we use in BE custom session plugin to add session.permissions key
		cachedSession = session as CustomSession | null;
		lastFetchedSession = Date.now();

		// Clear the promise cache so future calls can fetch again
		sessionPromise = null;

		return cachedSession;
	})();

	return sessionPromise;
};

const getPermissions = async () => {
	const session = await getCachedSession();
	if (!session) return [];
	return session.session.permissions || [];
};

export const authProvider: AuthProvider = {
	login: async ({ context, username }) => {
		try {
			return match(context as LoginMethod)
				.with("sso", async () => {
					const result = await authClient.signIn.social({
						provider: "google",
						callbackURL: `${basePath}${loginRedirectPath}`,
					});
					if (result.error) {
						throw new Error(result.error.message);
					}
					return Promise.resolve();
				})
				.with("magic_link", async () => {
					const result = await authClient.signIn.magicLink({
						email: username, // required
						name: username,
						callbackURL: `${basePath}${loginRedirectPath}`,
						newUserCallbackURL: `${basePath}${loginRedirectPath}`,
						errorCallbackURL: `${basePath}${loginRedirectPath}`,
					});
					if (result.error) {
						throw new Error(result.error.message);
					}
					return { redirectTo: false };
				})
				.exhaustive();
		} catch (error) {
			throw new Error(error instanceof Error ? error.message : "Login failed");
		}
	},

	logout: async () => {
		isLoggingOut = true;

		// Check if there's an active session before calling signOut
		// This prevents unnecessary API calls when unauthenticated users navigate to login
		// (e.g., clicking "Sign In" from public homepage)
		const hasActiveSession =
			cachedSession?.user !== null && cachedSession?.user !== undefined;

		clearSessionCache();

		// Only call signOut if there was an active session
		// This avoids 400 errors from better-auth when trying to sign out a non-existent session
		if (hasActiveSession) {
			try {
				await authClient.signOut();
			} catch (_error) {
				// ignore signOut errors – session is already cleared locally
			}
		}

		isLoggingOut = false;
		return Promise.resolve();
	},

	checkError: async (error: ErrorType) => {
		const status = error.status;
		// Check if this is a subscription limit error - don't sign out for these
		if (
			status === 403 &&
			!Array.isArray(error.body) &&
			error.body?.error.message &&
			isSubscriptionLimitError(error.body.error.message)
		) {
			// Don't sign out for subscription limit errors
			return Promise.resolve();
		}

		if (status === 401) {
			// Skip if a logout is already in progress to avoid re-entering the cycle
			if (isLoggingOut) {
				return Promise.reject();
			}
			isLoggingOut = true;
			clearSessionCache();
			try {
				await authClient.signOut();
			} finally {
				isLoggingOut = false;
			}
			return Promise.reject();
		}
		return Promise.resolve();
	},

	checkAuth: async () => {
		const session = await getCachedSession();
		if (session?.user) {
			return Promise.resolve();
		}
		return Promise.reject();
	},

	getPermissions,

	getIdentity: async () => {
		const session = await getCachedSession();
		const user = session?.user;

		if (user) {
			return Promise.resolve({
				id: user.id,
				fullName: user.email,
			});
		}
		return Promise.reject();
	},
	canAccess: async () => {
		// const permissions = await getPermissions();
		// return permissions.includes(`${resource}:${action}`);
		return true;
	},
};

export { authClient };
